// DashboardMenu.jsx
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { actions } from '../../util/actions';
import ReturnToI      from '../../assets/image/return-i-off.svg';
import AccountInfo    from '../../assets/image/account-info-off.svg';
import Curriculum     from '../../assets/image/curriculum-off.svg';
import Videos         from '../../assets/image/videos-off.svg';
import ModuleProgress from '../../assets/image/module-progress-off.svg';
import Certificate    from '../../assets/image/certificate-off.svg';
import AdminDashboard from '../../assets/image/admin-off.svg';
import useCurriculumLink from '../../hooks/useCurriculumLink';

const breakpoints = { tablet: 768, phone: 480 };

// wrap arrows + menu
const ScrollWrapper = styled.div`
  position: relative;
  margin-top: 20px;
  padding: 0 20px;
`;

// your existing container
const MenuContainer = styled.div`
  display: flex;
  gap: 30px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;

  @media (max-width: ${breakpoints.tablet}px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    width: 94%;
    padding: 0 10px;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
  }
`;

// arrow buttons
const ArrowButton = styled.button`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: transparent;
  border: none;
  padding: 6px;
  border-radius: 50%;
  color: white;
  cursor: pointer;

  @media (max-width: ${breakpoints.tablet}px) {
    display: block;
  }

  ${({ left }) => left
    ? `left: 0;`
    : `right: 0;`}
`;

const MenuCard = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  text-decoration: none;

  &:hover { transform: scale(1.05); }
  &:hover::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.3);
    border-radius: 10px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex: 0 0 auto;
    width: 20vw;
    max-width: 200px;
  }
`;

const MenuIcon = styled.img`
  width: 10vw;
  max-width: 130px;
  height: auto;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

export default function DashboardMenu({ curriculumLink }) {
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const rolesRaw = useSelector((s) => s.auth.user?.roles);
  const roles = useMemo(
    () => (Array.isArray(rolesRaw) ? rolesRaw : []),
    [rolesRaw]
  );
  const computedCurriculumLink = useCurriculumLink();
  const isSubAdmin   = roles.some(r => [
    "subscription_admin","subscription_access",
    "subscription_accountant","subscription_implementor",
    "subscription_salesperson"
  ].includes(r.id));
  const isISafeAdmin = roles.some(r => [
    "isafe_admin","isafe_moderator",
    "isafe_salesperson","isafe_accountant","root"
  ].includes(r.id));
  const isAnyAdmin = isSubAdmin || isISafeAdmin;
  const adminPath  = isSubAdmin
    ? "/admincentral/dashboard"
    : "/admincentral/isafeadmins/dashboard";

  const resolvedCurriculumLink = useMemo(() => {
    if (typeof curriculumLink === "string") {
      const trimmed = curriculumLink.trim();
      if (trimmed) return trimmed;
    }
    return computedCurriculumLink;
  }, [curriculumLink, computedCurriculumLink]);

  console.log("link", + resolvedCurriculumLink)

  const baseMenu = [
    { path: "/",                      icon: ReturnToI,       alt: "Home"             },
    { path: "/user/update",           icon: AccountInfo,     alt: "Account Info"     },
    resolvedCurriculumLink
      ? {
          href: resolvedCurriculumLink,
          icon: Curriculum,
          alt: "Curriculum",
          openInNewTab: true,
        }
      : { path: "/curriculum",            icon: Curriculum,      alt: "Curriculum"       },
    { path: "/myinfo/videos",         icon: Videos,          alt: "Videos"           },
    { path: "/myinfo/moduleprogress", icon: ModuleProgress,  alt: "Module Progress"  },
    { path: "/myinfo/certificate",    icon: Certificate,     alt: "Certificate"      },
  ];
  if (isAnyAdmin) {
    baseMenu.push({
      path: adminPath,
      icon: AdminDashboard,
      alt: "Admin Central",
    });
  }

  const scrollBy = dist => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dist, behavior: 'smooth' });
    }
  };

  const handleMenuClick = (type) => () => {
    if (type === 'videos') {
      dispatch({ type: actions.FETCH_RESOURCE_DOWNLOAD_ASYNC });
    } else if (type === 'ilearn') {
      dispatch({ type: actions.FETCH_ILEARN_CONTENT_ASYNC });
    }
  };

  return (
    <ScrollWrapper>
      <ArrowButton left onClick={() => scrollBy(-150)}>
        <FaChevronLeft />
      </ArrowButton>

      <MenuContainer ref={scrollRef}>
        {baseMenu.map(({ path, href, icon, alt, openInNewTab }) => {
          if (href) {
            return (
              <MenuCard
                as="a"
                href={href}
                target={openInNewTab ? "_blank" : undefined}
                rel={openInNewTab ? "noopener noreferrer" : undefined}
                key={alt}
              >
                <MenuIcon src={icon} alt={alt} />
              </MenuCard>
            );
          }
          return (
            <MenuCard
              to={path}
              key={alt}
              onClick={
                path === "/myinfo/videos"
                  ? handleMenuClick('videos')
                  : path === "/myinfo/moduleprogress"
                  ? handleMenuClick('ilearn')
                  : undefined
              }
            >
              <MenuIcon src={icon} alt={alt} />
            </MenuCard>
          );
        })}
      </MenuContainer>

      <ArrowButton onClick={() => scrollBy(150)}>
        <FaChevronRight />
      </ArrowButton>
    </ScrollWrapper>
  );
}
